import React, {useEffect, useState} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Grid,
    Link,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import {clearError, register} from '../../store/slices/authSlice';
import {AppDispatch, RootState} from '../../store';
import Logo from '../../components/common/Logo';

const RegisterPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const {isAuthenticated, isLoading, error} = useSelector(
        (state: RootState) => state.auth
    );

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [fullNameError, setFullNameError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard');
        }

        // Clear any errors when navigating to this page
        return () => {
            dispatch(clearError());
        };
    }, [isAuthenticated, navigate, dispatch]);

    const validateForm = (): boolean => {
        let isValid = true;

        // Validate full name
        if (!fullName) {
            setFullNameError('Full name is required');
            isValid = false;
        } else {
            setFullNameError('');
        }

        // Validate email
        if (!email) {
            setEmailError('Email is required');
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError('Invalid email address');
            isValid = false;
        } else {
            setEmailError('');
        }

        // Validate password
        if (!password) {
            setPasswordError('Password is required');
            isValid = false;
        } else if (password.length < 6) {
            setPasswordError('Password must be at least 6 characters');
            isValid = false;
        } else {
            setPasswordError('');
        }

        // Validate confirm password
        if (!confirmPassword) {
            setConfirmPasswordError('Please confirm your password');
            isValid = false;
        } else if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match');
            isValid = false;
        } else {
            setConfirmPasswordError('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            const resultAction = await dispatch(register({email, password, fullName}));

            if (register.fulfilled.match(resultAction)) {
                if (!resultAction.payload.user) {
                    // Registration successful but requires email confirmation
                    setSuccessMessage('Registration successful! Please check your email to confirm your account.');

                    // Clear the form
                    setFullName('');
                    setEmail('');
                    setPassword('');
                    setConfirmPassword('');
                }
            }
        }
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    py: 4,
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        width: '100%',
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{display: 'flex', justifyContent: 'center', mb: 4}}>
                        <Logo height={60}/>
                    </Box>

                    <Typography variant="h5" component="h1" align="center" gutterBottom>
                        Create an Account
                    </Typography>

                    <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
                        Sign up to start tracking your SEO performance
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{mb: 3}}>
                            {error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert severity="success" sx={{mb: 3}}>
                            {successMessage}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="fullName"
                            label="Full Name"
                            name="fullName"
                            autoComplete="name"
                            autoFocus
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            error={!!fullNameError}
                            helperText={fullNameError}
                            disabled={isLoading || !!successMessage}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!emailError}
                            helperText={emailError}
                            disabled={isLoading || !!successMessage}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!passwordError}
                            helperText={passwordError}
                            disabled={isLoading || !!successMessage}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={!!confirmPasswordError}
                            helperText={confirmPasswordError}
                            disabled={isLoading || !!successMessage}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{mt: 3, mb: 2}}
                            disabled={isLoading || !!successMessage}
                        >
                            {isLoading ? <CircularProgress size={24}/> : 'Sign Up'}
                        </Button>

                        <Grid container justifyContent="flex-end">
                            <Grid>
                                <Link component={RouterLink} to="/login" variant="body2">
                                    Already have an account? Sign in
                                </Link>
                            </Grid>
                        </Grid>
                    </form>

                </Paper>

                <Typography variant="body2" color="text.secondary" sx={{mt: 3}}>
                    © {new Date().getFullYear()} SEO Dashboard. All rights reserved.
                </Typography>
            </Box>
        </Container>
    );
};

export default RegisterPage;